/** Shared hour math for employee time tracking and payroll export. */

export const HOUR_TRACKING_ENTRY_TYPES = ['work'] as const;

/** Absence types credited with Sollstunden/Tag on weekdays (no start/end times). */
export const FIXED_DAY_HOURS_ENTRY_TYPES = ['vacation', 'sickness', 'overtime_comp', 'special_leave'] as const;

/** Exported with Kalenderschlüssel and 0 Stunden (Tage = 1) on weekdays. */
export const ZERO_HOUR_EXPORT_ENTRY_TYPES = ['unpaid_absence', 'sickness_benefit'] as const;
export type HourTrackingEntryType = (typeof HOUR_TRACKING_ENTRY_TYPES)[number];

export function isHourTrackingEntryType(type: string): type is HourTrackingEntryType {
  return (HOUR_TRACKING_ENTRY_TYPES as readonly string[]).includes(type);
}

export function isFixedDayHoursEntryType(type: string): boolean {
  return (FIXED_DAY_HOURS_ENTRY_TYPES as readonly string[]).includes(type);
}

export function isZeroHourExportEntryType(type: string): boolean {
  return (ZERO_HOUR_EXPORT_ENTRY_TYPES as readonly string[]).includes(type);
}

export function toHm(time: string | null): string {
  if (!time) return '';
  return time.slice(0, 5);
}

export function calcGrossHours(start: string | null, end: string | null): number {
  if (!start || !end) return 0;
  const [sh, sm] = toHm(start).split(':').map((x) => Number.parseInt(x, 10));
  const [eh, em] = toHm(end).split(':').map((x) => Number.parseInt(x, 10));
  if (!Number.isFinite(sh) || !Number.isFinite(sm) || !Number.isFinite(eh) || !Number.isFinite(em)) return 0;
  const mins = eh * 60 + em - (sh * 60 + sm);
  if (mins <= 0) return 0;
  return mins / 60;
}

export function normalizeBreakMinutes(value: unknown): number {
  const n = Math.floor(Number(value));
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(n, 24 * 60);
}

export function calcWorkHours(start: string | null, end: string | null, breakMinutes?: number | null): number {
  const gross = calcGrossHours(start, end);
  const br = normalizeBreakMinutes(breakMinutes);
  return Math.max(0, gross - br / 60);
}

/** § 2 ArbZG: Arbeitszeit vom Beginn bis Ende ohne Ruhepausen (Minuten). */
export function calcNetWorkMinutes(
  start: string | null,
  end: string | null,
  breakMinutes?: number | null,
): number {
  return Math.round(calcWorkHours(start, end, breakMinutes) * 60);
}

/** Mindestpause in Minuten nach § 4 ArbZG für gegebene Netto-Arbeitszeit. */
export function minimumBreakMinutesForArbeitszeit(netWorkMinutes: number): number {
  if (netWorkMinutes <= 6 * 60) return 0;
  if (netWorkMinutes <= 9 * 60) return 30;
  return 45;
}

/** Up to 2 fractional digits; trailing zeros omitted (10, 10,5, 10,75). */
export function formatDisplayHours(value: number): string {
  if (!Number.isFinite(value)) return '';
  const rounded = Math.round(value * 100) / 100;
  return rounded.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

export function normalizeYmd(value: string | null | undefined): string | null {
  const s = String(value ?? '').trim().slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
}

export function isWeekdayYmd(ymd: string): boolean {
  const normalized = normalizeYmd(ymd);
  if (!normalized) return false;
  const [y, m, d] = normalized.split('-').map((x) => Number.parseInt(x, 10));
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return false;
  const day = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return day >= 1 && day <= 5;
}

/** Entry falls within employment window (inclusive). Missing start/end = open bound. */
export function isWithinEmployment(
  entryDate: string,
  employeeStartDate: string | null | undefined,
  employeeEndDate?: string | null | undefined,
): boolean {
  const d = normalizeYmd(entryDate);
  if (!d) return false;
  const start = normalizeYmd(employeeStartDate);
  const end = normalizeYmd(employeeEndDate);
  if (start && d < start) return false;
  if (end && d > end) return false;
  return true;
}

export function isHolidayCreditDate(entryDate: string, employeeStartDate: string | null): boolean {
  const d = normalizeYmd(entryDate);
  const start = normalizeYmd(employeeStartDate);
  return Boolean(d && start && d >= start && isWeekdayYmd(d));
}

export function parseTimeToMinutes(time: string | null): number | null {
  const hm = toHm(time);
  if (!hm) return null;
  const [h, m] = hm.split(':').map((x) => Number.parseInt(x, 10));
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
}

export function isEndAfterStart(start: string | null, end: string | null): boolean {
  const startMins = parseTimeToMinutes(start);
  const endMins = parseTimeToMinutes(end);
  if (startMins == null || endMins == null) return false;
  return endMins > startMins;
}

/**
 * Hours for a payroll CSV row, or `null` if the entry should not be exported.
 */
export function resolvePayrollExportHours(params: {
  entryType: string;
  entryDate: string;
  startTime: string | null;
  endTime: string | null;
  breakMinutes?: number | null;
  expectedHoursPerDay: number;
  employeeStartDate: string | null;
  employeeEndDate?: string | null;
}): number | null {
  const {
    entryType,
    entryDate,
    startTime,
    endTime,
    breakMinutes,
    expectedHoursPerDay,
    employeeStartDate,
    employeeEndDate,
  } = params;

  if (entryType === 'shift_free') return null;

  if (!isWithinEmployment(entryDate, employeeStartDate, employeeEndDate)) return null;

  if (isHourTrackingEntryType(entryType)) {
    return calcWorkHours(startTime, endTime, breakMinutes);
  }

  if (isFixedDayHoursEntryType(entryType)) {
    if (!isWeekdayYmd(entryDate)) return null;
    return expectedHoursPerDay;
  }

  if (entryType === 'public_holiday') {
    if (!isHolidayCreditDate(entryDate, employeeStartDate)) return null;
    return expectedHoursPerDay;
  }

  if (isZeroHourExportEntryType(entryType)) {
    if (!isWeekdayYmd(entryDate)) return null;
    return 0;
  }

  return null;
}

/**
 * Hours for Mitarbeiter-Übersicht. Differs from payroll export for types that export 0 h
 * but should still appear with Sollstunden/Tag in the overview (Krankengeld, Fehlzeit).
 */
export function resolveOverviewHours(params: {
  entryType: string;
  entryDate: string;
  startTime: string | null;
  endTime: string | null;
  breakMinutes?: number | null;
  expectedHoursPerDay: number;
  employeeStartDate: string | null;
  employeeEndDate?: string | null;
}): number | null {
  const { entryType, entryDate, expectedHoursPerDay, employeeStartDate, employeeEndDate } = params;

  if (entryType === 'shift_free') return null;
  if (!isWithinEmployment(entryDate, employeeStartDate, employeeEndDate)) return null;

  if (entryType === 'sickness_benefit' || entryType === 'unpaid_absence') {
    if (!isWeekdayYmd(entryDate)) return null;
    return expectedHoursPerDay;
  }

  return resolvePayrollExportHours(params);
}

function ymdFromUtcDate(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseYmdUtc(ymd: string): Date | null {
  const normalized = normalizeYmd(ymd);
  if (!normalized) return null;
  const [y, m, d] = normalized.split('-').map((x) => Number.parseInt(x, 10));
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  return new Date(Date.UTC(y, m - 1, d));
}

/** Every calendar day from `from` through `to` (inclusive), as YYYY-MM-DD. */
export function* eachYmdInInclusiveRange(from: string, to: string): Generator<string> {
  const start = parseYmdUtc(from);
  const end = parseYmdUtc(to);
  if (!start || !end || start.getTime() > end.getTime()) return;
  const cur = new Date(start);
  while (cur.getTime() <= end.getTime()) {
    yield ymdFromUtcDate(cur);
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
}

/**
 * Soll-Arbeitsstunden im Zeitraum: Werktage innerhalb der Beschäftigung × Sollstunden/Tag,
 * abzüglich geplanter Freischicht-Werktage (reduzieren das Soll, vgl. Zeiterfassung).
 */
export function computeTargetWorkHoursInRange(params: {
  from: string;
  to: string;
  expectedHoursPerDay: number;
  employeeStartDate: string | null | undefined;
  employeeEndDate?: string | null | undefined;
  shiftFreeWeekdayDates?: Iterable<string>;
}): number {
  const expected =
    Number.isFinite(params.expectedHoursPerDay) && params.expectedHoursPerDay > 0
      ? params.expectedHoursPerDay
      : 8;
  const shiftFree = new Set<string>();
  for (const d of params.shiftFreeWeekdayDates ?? []) {
    const n = normalizeYmd(d);
    if (n && isWeekdayYmd(n)) shiftFree.add(n);
  }
  let weekdays = 0;
  for (const ymd of eachYmdInInclusiveRange(params.from, params.to)) {
    if (!isWeekdayYmd(ymd)) continue;
    if (!isWithinEmployment(ymd, params.employeeStartDate, params.employeeEndDate)) continue;
    if (shiftFree.has(ymd)) continue;
    weekdays += 1;
  }
  return Math.round(weekdays * expected * 100) / 100;
}

/** Auslastung in Prozent (Zähler / Soll-Arbeit), gerundet. */
export function computeWorkUtilizationPct(numeratorHours: number, targetHours: number): number | null {
  if (!Number.isFinite(targetHours) || targetHours <= 0) return null;
  const numerator = Number.isFinite(numeratorHours) && numeratorHours > 0 ? numeratorHours : 0;
  return Math.round((numerator / targetHours) * 100);
}

/**
 * Übersicht-Auslastung: (Ist-Arbeit + Urlaub + Feiertag + Krank) / Soll-Arbeit × 100.
 * Ist-Arbeit = Arbeit − Stundenausgleich.
 */
export function computeOverviewUtilizationPct(params: {
  actualWorkHours: number;
  vacationHours: number;
  sicknessHours: number;
  publicHolidayHours: number;
  targetWorkHours: number;
}): number | null {
  const numerator = Math.round(
    (Math.max(0, params.actualWorkHours) +
      Math.max(0, params.vacationHours) +
      Math.max(0, params.sicknessHours) +
      Math.max(0, params.publicHolidayHours)) *
      100,
  ) / 100;
  return computeWorkUtilizationPct(numerator, params.targetWorkHours);
}
